// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { initializeUser, getUserProfile, updateUserProfile as updateUserProfileApi } from '../utils/userApi';

interface UserProfile {
  user_id: string;
  email?: string;
  display_name?: string;
  photo_url?: string;
  is_anonymous: boolean;
  subscription_tier: 'free' | 'pro' | 'enterprise';
  created_at?: string;
  updated_at?: string;
  projects: string[];  // Changed from collections to projects
  project_count: number;  // Changed from collection_count to project_count
  preferences: Record<string, any>;
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAuthenticating: boolean;
  isLoggingOut: boolean;
  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  // Profile management
  refreshUserProfile: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Auth providers
  const googleProvider = new GoogleAuthProvider();
  const githubProvider = new GithubAuthProvider();

  // Configure providers
  googleProvider.addScope('email');
  googleProvider.addScope('profile');
  githubProvider.addScope('user:email');

  /**
   * Initialize user in our backend after Firebase authentication
   */
  const initializeUserInBackend = async (user: User): Promise<void> => {
    try {
      const userData = await initializeUser();
      console.log('User initialized in backend:', userData);
      setUserProfile(userData.user);
    } catch (error) {
      console.error('Error initializing user in backend:', error);
      // Don't throw error here, just log it
    }
  };

  /**
   * Fetch user profile from backend using API utils
   */
  const refreshUserProfile = async (): Promise<void> => {
    if (!currentUser) {
      setUserProfile(null);
      return;
    }

    try {
      const profileData = await getUserProfile();
      setUserProfile(profileData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Try to initialize user if profile fetch fails
      await initializeUserInBackend(currentUser);
    }
  };

  /**
   * Update user profile using API utils
   */
  const updateUserProfile = async (updates: Partial<UserProfile>): Promise<void> => {
    if (!currentUser) return;

    try {
      const updatedData = await updateUserProfileApi({
        display_name: updates.display_name,
        preferences: updates.preferences,
      });
      setUserProfile(updatedData.user);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  // Helper function to add smooth transitions
  const withAuthTransition = async <T,>(
    authFunction: () => Promise<T>,
    setTransitionState: (state: boolean) => void
  ): Promise<T> => {
    setTransitionState(true);
    try {
      const result = await authFunction();
      // Add a small delay to prevent jarring instant transitions
      await new Promise(resolve => setTimeout(resolve, 500));
      return result;
    } finally {
      setTransitionState(false);
    }
  };

  // Email/password auth
  const signup = async (email: string, password: string, displayName: string): Promise<void> => {
    await withAuthTransition(async () => {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      // Backend initialization will happen in onAuthStateChanged
    }, setIsAuthenticating);
  };

  const login = async (email: string, password: string): Promise<void> => {
    await withAuthTransition(async () => {
      await signInWithEmailAndPassword(auth, email, password);
      // Backend initialization will happen in onAuthStateChanged
    }, setIsAuthenticating);
  };

  const logout = async (): Promise<void> => {
    await withAuthTransition(async () => {
      await signOut(auth);
      setUserProfile(null);
    }, setIsLoggingOut);
  };

  // Social auth methods
  const signInWithGoogle = async (): Promise<void> => {
    await withAuthTransition(async () => {
      await signInWithPopup(auth, googleProvider);
      // Backend initialization will happen in onAuthStateChanged
    }, setIsAuthenticating);
  };

  const signInWithGithub = async (): Promise<void> => {
    await withAuthTransition(async () => {
      await signInWithPopup(auth, githubProvider);
      // Backend initialization will happen in onAuthStateChanged
    }, setIsAuthenticating);
  };

  // Set up auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // User is signed in, initialize them in our backend
        await initializeUserInBackend(user);
      } else {
        // User is signed out
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Refresh user profile when currentUser changes (but not on initial load)
  useEffect(() => {
    if (currentUser && !loading && !userProfile) {
      refreshUserProfile();
    }
  }, [currentUser?.uid, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    isAuthenticating,
    isLoggingOut,
    login,
    signup,
    logout,
    signInWithGoogle,
    signInWithGithub,
    refreshUserProfile,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};